import { isAxiosError } from "axios";
import { publicSurveyAxios } from "../configs/axios";

export const SURVEY_TEXT_MAX_LENGTH = 5000;

export type SurveyQuestionType = "SINGLE" | "MULTI" | "TEXT" | "DATE";
export type SurveyImageAspectRatio = "1:1" | "16:9";

export const SURVEY_RESPONSE_LIMIT_MESSAGE =
  "このアンケートは回答回数の上限に達しています。";
export const SURVEY_VALIDATION_ERROR_MESSAGE =
  "アンケートの入力内容を確認してください。";

export type SurveyPublicChoice = {
  id: number;
  label: string;
};

export type SurveyPublicAnswer = {
  choiceIds?: number[];
  textValue?: string | null;
  dateValue?: string | null;
};

export type SurveyPublicQuestion = {
  id: number;
  sortOrder: number;
  type: SurveyQuestionType;
  title: string;
  required: boolean;
  maxSelections?: number | null;
  imageUrl?: string | null;
  imageAspectRatio?: SurveyImageAspectRatio | null;
  choices?: SurveyPublicChoice[];
  answered: boolean;
  answer?: SurveyPublicAnswer;
};

export type SurveyPublicDetail = {
  surveyId: number;
  thanksMessage?: string | null;
  isCompleted: boolean;
  responseLimit: number | null;
  completedResponseCount: number;
  questions: SurveyPublicQuestion[];
};

export type SurveyAnswerInput = {
  questionId: number;
  choiceIds?: number[];
  textValue?: string;
  dateValue?: string;
};

export type SurveyPublicRequest = {
  surveyId: number;
  surveyToken: string;
};

export type SurveySubmitRequest = SurveyPublicRequest & {
  answers: SurveyAnswerInput[];
};

export type SurveySubmitResponse = {
  success: boolean;
  message: string;
};

type SurveyApiErrorResponse = {
  message?: string | string[];
};

export const getSurveyApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (!isAxiosError<SurveyApiErrorResponse>(error)) return fallback;

  const message = error.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (Array.isArray(message)) {
    if (message.length > 0) return SURVEY_VALIDATION_ERROR_MESSAGE;
  }
  return fallback;
};

export const shouldReconcileSurveySubmit = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  if (getSurveyApiErrorMessage(error, "") === SURVEY_RESPONSE_LIMIT_MESSAGE) {
    return false;
  }
  return (
    error.response === undefined ||
    error.response.status === 409 ||
    error.response.status >= 500
  );
};

export const fetchSurveyDetail = async (
  params: SurveyPublicRequest,
): Promise<SurveyPublicDetail> => {
  const response = await publicSurveyAxios.post<SurveyPublicDetail>(
    "survey/public/detail",
    params,
  );
  return response.data;
};

export const submitSurvey = async (
  params: SurveySubmitRequest,
): Promise<SurveySubmitResponse> => {
  const response = await publicSurveyAxios.post<SurveySubmitResponse>(
    "survey/public/submit",
    params,
  );
  return response.data;
};
